import { Box, Dialog, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import AppShell, { WhiteBox } from 'components/AppShell'
import CustomButton from 'components/CustomButton'
import TextField, { TextArea } from 'components/CustomFields'
import { FileUploadSection } from 'components/FileUploadSection'
import { useServices } from 'contexts'
import { useNavigateWithClient } from 'hooks'
import React, { useState } from 'react'
import { createResumeRecord } from 'services'
import { analyseResume } from 'services/shortlistingService'
import { Session } from 'utils'

const useStyles = makeStyles((theme) => ({
  container: {
    width: '640px',
    border: `2px solid ${theme.palette.neutral['clr-200']}`,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(10),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(5)
  },
  backButton: {
    alignSelf: 'flex-start',
    marginBottom: theme.spacing(2),
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.primary.main
  },
  jobDescriptionBox: {
    border: `1px solid ${theme.palette.neutral['clr-200']}`,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(3),
    minHeight: '200px',
    width: '100%',
    resize: 'vertical',
    fontFamily: 'inherit',
    fontSize: '14px',
    outline: 'none',
    '&:focus': {
      borderColor: theme.palette.primary.main
    }
  },
  dialogContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(3),
    height: '100%',
    width: '100%',
    padding: theme.spacing(10),
    position : 'relative',
    overflow : 'hidden'
  },
  circularGifBot: {
    position: 'absolute',
    left: '-150px',
    bottom : '-135px',
    opacity: '10%'
  },
  circularGifTop: {
    position: 'absolute',
    right: '-150px',
    top : '-135px',
    opacity: '10%'
  },
}))

const ReviewComponent = ({ resumeOnly = false }) => {
  const classes = useStyles()
  const service = useServices();
  const navigateWithClient = useNavigateWithClient()

  const [currentStep, setCurrentStep] = useState(1)
  // Remove jobRoles, companies, companiesLoading, loading
  const [formData, setFormData] = useState({
    jobRole: '',
    companyName: '',
    jobDescription: '',
    resume: null
  })


  const handleFileProcessed = async({ fileName, scrapedData}) => {
    try {
      let payload = {
        user : Session.userId,
        name : fileName,
        parsedData : scrapedData.extractedText,
        path : scrapedData.filePath
      };
      if(scrapedData){
        let resumeRecord = await service.callService(createResumeRecord, payload)
        handleInputChange('resume', resumeRecord)
      }
    } catch (error) {
      console.error('Error processing file:', error);
    } 
  };

  const handleCheckRelevanceScore = async() => {
    let payload = {
      user : Session.userId,
      resume : formData.resume._id,
      jobRole : formData.jobRole,
      jobDescription: formData?.jobDescription,
      company: formData?.companyName,
    }
    let result = await service.callService(analyseResume, payload)
    if(result){
      navigateWithClient(`/performance/shortlist-genie/${result._id}`)
      const currentValue = parseInt(localStorage.getItem('resumeAnalyseCount')) || 0;
      localStorage.setItem('resumeAnalyseCount', (currentValue + 1).toString());
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }


  const handleNext = async () => {
    if (resumeOnly) {
      if (currentStep === 1) {
        setCurrentStep(2);
        await handleCheckRelevanceScore();
      }
    } else {
      if (currentStep === 1) {
        setCurrentStep(2);
      } else if (currentStep === 2) {
        setCurrentStep(3);
        await handleCheckRelevanceScore();
      }
    }
  }

  const handleBack = () => {
    setCurrentStep(currentStep - 1)
  }

  const canProceed = () => {
    if (resumeOnly) {
      return formData.jobRole && formData.resume
    } else {
      if (currentStep === 1) {
        return formData.jobRole && formData.companyName && formData.resume
      } else if (currentStep === 2) {
        return formData.jobDescription.trim() !== ''
      }
    }
    return false
  }


  const renderStep1 = () => (
    <Box className={classes.container}>
      <Typography variant='h5-medium' color='primary.clr-400'>
        {resumeOnly ? 'Instant Resume Review' : 'JD & Resume Relevance Review'}
      </Typography>
    
      <TextField
        name="jobRole"
        value={formData.jobRole}
        label="Enter Job Role"
        style={{ width: '100%' }}
        onChange={e => handleInputChange('jobRole', e.target.value)}
        width="100%"
        placeholder="Enter job Role"
      />

    {!resumeOnly && (
      <TextField
        name="companyName"
        value={formData.companyName}
        label="Enter Company Name"
        style={{ width: '100%' }}
        placeholder="Enter Company Name"
        onChange={e => handleInputChange('companyName', e.target.value)}
        width="100%"
      />
    )}

    <FileUploadSection
      title='Your Resume'
      fileType="resume"
      iconSrc="https://assets.languify.in/images/resumeDoc.png"
      uploadName="resume"
      changeText="job description"
      width='100%'
      showDate={true}
      onFileProcessed={handleFileProcessed}
      uploadToS3={true}
    />

    <CustomButton
      size='medium'
      onClick={handleNext}
      disabled={!canProceed()}
    >
      {resumeOnly ? 'Analyze now' : 'Continue to add job description'}
    </CustomButton>
  </Box>
  )

  const renderStep2JobDescription = () => (
    <Box className={classes.container}>
      <Box className={classes.backButton} onClick={handleBack}>
        <img
          src="https://assets.languify.in/images/arrow-left-icon.png"
          alt="moveLeftIcon"
          width={36}
          height={36}
        />
        <Typography variant='h5-medium' color='primary.clr-400'>
          JD & Resume Relevance Review
        </Typography>
      </Box>
      
      <TextArea
        name="jobDescription"
        label='Job description'
        rows={8}
        placeholder="Paste company job description here..."
        value={formData.jobDescription}
        style={{ width: '100%' }}
        onChange={(e) => handleInputChange('jobDescription', e.target.value)}
      />

      <CustomButton
        size='medium'
        onClick={handleNext}
        disabled={!canProceed()}
      >
        Analyze now
      </CustomButton>

      {/* <Typography variant='body01-medium' sx={{ textAlign: 'center' }}>
        1 credit will be used for analyzing.
      </Typography> */}
    </Box>
  )

  const renderAnalyzingScreen = () => (
    <Dialog 
      open={true}  
      sx={{
        '& .MuiDialog-paper': {
          width: '720px', 
          height: '440px',
        },
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 17, 38, 0.7)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        },
      }}
    >
      <Box className={classes.dialogContainer}>
        <img
          src='https://assets.languify.in/images/aiAssessment.gif'
          alt='gif'
          width={108}
          height={108}
        />
        <img
          src='https://assets.languify.in/images/circlularGifPersonalized.gif'
          alt='gif'
          width={298}
          height={298}
          className={classes.circularGifBot}
        />
        <img
          src='https://assets.languify.in/images/circlularGifPersonalized.gif'
          alt='gif'
          width={298}
          height={298}
          className={classes.circularGifTop}
        />
        <Typography variant='h6-medium' color='neutral.clr-900' sx={{maxWidth : '342px' , textAlign : 'center'}}>
        Analyzing how well your resume matches this job profile…
        </Typography>
      </Box>
    </Dialog>
  )

  const renderCurrentStep = () => {
    if (resumeOnly) {
      switch (currentStep) {
        case 1:
          return renderStep1()
        case 2:
          return renderAnalyzingScreen()
        default:
          return renderStep1()
      }
    } else {
      switch (currentStep) {
        case 1:
          return renderStep1()
        case 2:
          return renderStep2JobDescription()
        case 3:
          return renderAnalyzingScreen()
        default:
          return renderStep1()
      }
    }
  }

  return (
    <AppShell headerLabel='Shortlisting Genie'>
      <WhiteBox
        sx={{
          height: '80vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        {renderCurrentStep()}
      </WhiteBox>
    </AppShell>
  )
}

export default ReviewComponent